import React, { useEffect, useState } from "react";
import useDelivery from "../../services/Order/useDelivery";
import { toast } from "react-toastify";
import LoadingScreen from "../../utils/LoadingScreen";
import { RotateCcw } from "lucide-react";
import { formatDateTime } from "../../utils/dateFormatter";

const Delivery = () => {
  const {
    deliveryPersons,
    getDeliveryPersons,
    addDeliveryPerson,
    deleteDeliveryPerson,
  } = useDelivery();

  const [showAddModal, setShowAddModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [loading, setLoading] = useState(false);

  const [form, setForm] = useState({
    name: "",
    mobile: "",
    area: "",
  });

  const [isRefreshing, setIsRefreshing] = useState(false);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    setLoading(true);

    await getDeliveryPersons();

    setLoading(false);
    setIsRefreshing(false);
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      await getDeliveryPersons();
      setLoading(false);
    };

    fetchData();
  }, []);

  const [errors, setErrors] = useState({
    name: "",
    mobile: "",
    area: "",
  });

  const handleAdd = async () => {
    let newErrors = {};

    // Name validation
    if (!form.name || form.name.trim() === "") {
      newErrors.name = "Name is required";
    }

    // Area validation
    if (!form.area || form.area.trim() === "") {
      newErrors.area = "Area is required";
    }

    // Mobile validation
    if (!form.mobile) {
      newErrors.mobile = "Mobile number is required";
    } else if (!/^[0-9]{10}$/.test(form.mobile)) {
      newErrors.mobile = "Mobile number must be 10 digits";
    }

    setErrors(newErrors);

    //  STOP API CALL
    if (Object.keys(newErrors).length > 0) return;

    const res = await addDeliveryPerson(form);

    if (res?.responseCode === "00") {
      toast.success("Delivery Partner Added Successfully");

      setShowAddModal(false);
      setForm({ name: "", mobile: "", area: "" });
      setErrors({});

      await getDeliveryPersons();
    } else {
      toast.error(res?.responseMessage || "Something went wrong");
    }
  };

  const handleDelete = async () => {
    const res = await deleteDeliveryPerson(selectedId);

    if (res?.responseCode === "00") {
      toast.success("Delivery Partner Deleted ");

      setShowDeleteModal(false);
      setSelectedId(null);

      await getDeliveryPersons();
    } else {
      toast.error(res?.responseMessage || "Delete failed ");
    }
  };

  return (
    <>

    <div className="p-5 min-h-0 h-full flex flex-col overflow-hidden montserrat-font-1">
      <div className="flex justify-between items-center mb-4 montserrat-font-1">
        <h1 className="text-xl font-semibold">Delivery Partner Management</h1>

        <div className="flex items-center gap-2">
          {/*  Refresh Button */}
          <button
            onClick={handleRefresh}
            disabled={isRefreshing}
            className="border border-gray-300 bg-blue-500 text-white px-3 py-2 rounded-md flex items-center gap-1 disabled:opacity-50"
          >
            <RotateCcw
              className={`w-4 h-4 ${isRefreshing ? "animate-spin" : ""}`}
            />
            Refresh
          </button>

          {/*  Add Button */}
          <button
            onClick={() => setShowAddModal(true)}
            className="bg-green-600 text-white px-4 py-2 rounded-lg"
          >
            Add Delivery Partner
          </button>
        </div>
      </div>

      <div className="bg-white rounded overflow-auto h-full shadow montserrat-font-1">
        <table className="w-full text-sm text-start montserrat-font-1">
          <thead className="bg-green-100 sticky top-0 z-10">
            <tr>
              <th className="p-3 text-start">S.No.</th>
              <th className="p-3 text-start">Name</th>
              <th className="p-3 text-start">Mobile</th>
              <th className="p-3 text-start">Area</th>
              <th className="p-3 text-start">Orders</th>
              <th className="p-3 text-start">Created</th>
              <th className="p-3 text-start">Action</th>
            </tr>
          </thead>

          <tbody>
            {loading ? (
              <tr>
                <td colSpan="7" className="text-center py-10">
                  <LoadingScreen text="Loading delivery partners..." />
                </td>
              </tr>
            ) : deliveryPersons.length === 0 ? (
              <tr>
                <td colSpan="7" className="text-center py-6 text-gray-500">
                  No Delivery Partners Found
                </td>
              </tr>
            ) : (
              deliveryPersons.map((item, i) => (
                <tr key={item.id} className="border-t">
                  <td className="p-3">{i + 1}</td>
                  <td>{item.contact_name}</td>
                  <td>{item.contact_number}</td>
                  <td>{item.location}</td>
                  <td>{item.active_order_count}</td>
                  <td>{formatDateTime(item.created_on, true)}</td>
                  <td>
                    <button
                      onClick={() => {
                        setSelectedId(item.id);
                        setShowDeleteModal(true);
                      }}
                      className="border border-red-500 text-red-500 px-2 py-1 rounded text-xs"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/*  ADD MODAL */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center montserrat-font-1 z-50">
          <div className="bg-white w-[420px] rounded-xl shadow-lg montserrat-font-1">
            {/* Header */}
            <div className="flex justify-between items-center px-5 py-4 border-b ">
              <h2 className="text-lg font-semibold">Add Delivery Partner</h2>
              <button
                onClick={() => setShowAddModal(false)}
                className="text-gray-500 hover:text-black text-lg"
              >
                ✕
              </button>
            </div>

            {/* Body */}
            <div className="p-5 space-y-4">
              {/* Name */}
              <div>
                <label className="block text-sm font-medium mb-1">Name</label>
                <input
                  type="text"
                  placeholder="Enter Name"
                  className={`w-full border px-3 py-2 rounded-md text-sm outline-none
                    ${
                      errors.name
                        ? "border-red-500 focus:ring-red-500"
                        : "border-gray-300 focus:border-blue-500 focus:ring-blue-500"
                    }`}
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                />
              </div>

              {/* Delivery Area */}
              <div>
                <label className="block text-sm font-medium mb-1">
                  Delivery Area
                </label>
                <input
                  type="text"
                  placeholder="Enter Delivery Area"
                  className={`w-full border px-3 py-2 rounded-md text-sm outline-none
                    ${
                      errors.area
                        ? "border-red-500 focus:ring-red-500"
                        : "border-gray-300 focus:border-blue-500 focus:ring-blue-500"
                    }`}
                  value={form.area}
                  onChange={(e) => setForm({ ...form, area: e.target.value })}
                />
              </div>

              {/* Mobile */}
              <div>
                <label className="block text-sm font-medium mb-1">
                  Mobile No.
                </label>

                <input
                  type="text"
                  placeholder="Enter Mobile Number"
                  maxLength={10}
                  className={`w-full border px-3 py-2 rounded-md text-sm outline-none
                    ${
                      errors.mobile
                        ? "border-red-500 focus:ring-red-500"
                        : "border-gray-300 focus:border-blue-500 focus:ring-blue-500"
                    }`}
                  value={form.mobile}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, "");
                    setForm({ ...form, mobile: value });

                    if (value.length === 10) {
                      setErrors({ ...errors, mobile: "" });
                    }
                  }}
                />

                {errors.mobile && (
                  <p className="text-red-500 text-xs mt-1">{errors.mobile}</p>
                )}
              </div>
            </div>

            {/* Footer */}
            <div className="flex justify-end gap-2 px-5 py-4 border-t">
              <button
                onClick={() => setShowAddModal(false)}
                className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded-md text-sm"
              >
                Close
              </button>

              <button
                onClick={handleAdd}
                className="bg-blue-600 text-white px-4 py-2 rounded-md text-sm flex items-center gap-2"
              >
                {LoadingScreen ? "Add" : "Add Person"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/*  DELETE MODAL */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center montserrat-font-1 justify-center">
          <div className="bg-white w-[400px] rounded-lg p-5 text-center montserrat-font-1">
            <h2 className="text-lg font-semibold mb-4">Are you sure?</h2>

            <p className="text-gray-500 mb-5">
              Do you really want to delete this delivery partner?
            </p>

            <div className="flex justify-center gap-3">
              <button
                onClick={() => setShowDeleteModal(false)}
                className="bg-gray-400 text-white px-4 py-2 rounded"
              >
                Cancel
              </button>

              <button
                onClick={handleDelete}
                className="bg-red-600 text-white px-4 py-2 rounded flex items-center gap-2"
              >
                {LoadingScreen ? "Delete" : "Yes, Delete"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
    </>
  );
};

export default Delivery;
